/**
 * B2C self-assessment ES display helpers — category-level only (no item hazard names).
 * Scoring rubrics match Albert-backend/utils/aipScoringRubrics.js.
 */

import {
  countChecked,
  scoreFallHazardsFromCount,
  scoreBathroomFromHazardCount,
  scoreKitchenFromHazardCount,
  scoreHomeLayoutFromBarrierCount,
} from "./aipScoringRubrics";

export const B2C_SELF_ASSESSMENT_SOURCE = "b2c_self_assessment";

export const isB2cSelfAssessment = (data = {}) =>
  data?.assessmentSource === B2C_SELF_ASSESSMENT_SOURCE;

const ES_CATEGORIES = [
  {
    id: "fallHazards",
    title: "Fall Hazards",
    areaLabel: "fall hazards",
    keys: [
      "esLooseRugs",
      "esClutterWalkways",
      "esPoorLighting",
      "esStairsNoRailings",
      "esSlipperyFloors",
      "esNoGrabBars",
      "esUnevenSteps",
      "esCordsOnFloor",
      "esUnevenFlooring",
      "esFootwearNoTraction",
      "esNoNightlights",
      "esPetsUnderfoot",
      "esUnstableFurniture",
      "esItemsStoredImproper",
      "esOtherFallHazard",
    ],
    scoreFromCount: scoreFallHazardsFromCount,
    maxScore: 5,
  },
  {
    id: "bathroomSafety",
    title: "Bathroom Safety",
    areaLabel: "bathroom safety",
    keys: [
      "esUnstableShowerChair",
      "esWetSlipperyFloors",
      "esNoBathroomGrabBars",
      "esSlipperyTub",
      "esNoNonSlipStrips",
      "esLooseBathMats",
      "esLowToiletHeight",
      "esLooseToiletSeat",
      "esHighTubThreshold",
      "esBathroomPoorLighting",
      "esBathroomNoNightlight",
      "esBathroomClutter",
      "esItemsOutOfReach",
      "esTowelBarsForSupport",
      "esCrampedBathroom",
      "esOtherBathroomHazard",
    ],
    scoreFromCount: scoreBathroomFromHazardCount,
    maxScore: 4,
  },
  {
    id: "kitchenSafety",
    title: "Kitchen Safety",
    areaLabel: "kitchen safety",
    keys: [
      "esKitchenWetFloors",
      "esGreaseOnFloor",
      "esLooseKitchenMats",
      "esNoNonSlipBacking",
      "esReachingHighCabinets",
      "esUnsafeStepStool",
      "esKitchenClutter",
      "esNoFireExtinguisher",
      "esBendingLowCabinets",
      "esLooseDrawersDoors",
      "esFrayedCords",
      "esKitchenPoorLighting",
      "esApplianceCordsInPath",
      "esKitchenUnevenFlooring",
      "esOverloadedOutlet",
      "esNoSmokeDetector",
      "esNoCO2Detector",
      "esOtherKitchenHazard",
    ],
    scoreFromCount: scoreKitchenFromHazardCount,
    maxScore: 4,
  },
  {
    id: "homeLayout",
    title: "Home Layout & Accessibility",
    areaLabel: "home layout",
    keys: [
      "esStepsThresholds",
      "esNarrowHallways",
      "esLayoutClutter",
      "esInaccessibleBathroom",
      "esMultiLevelHome",
      "esTightDoorways",
      "esObstructedPathways",
    ],
    scoreFromCount: scoreHomeLayoutFromBarrierCount,
    maxScore: 4,
  },
];

export const categoryTierLabel = (score, maxScore) => {
  const max = Number(maxScore) || 1;
  const value = Number(score);
  if (!Number.isFinite(value)) return "Unknown";
  const ratio = value / max;
  if (ratio >= 0.75) return "Good";
  if (ratio >= 0.5) return "Moderate";
  return "At Risk";
};

export const getEsCategorySummaries = (data = {}) =>
  ES_CATEGORIES.map((cat) => {
    const hazardCount = countChecked(data, cat.keys);
    const categoryScore = cat.scoreFromCount(hazardCount);
    const tier = categoryTierLabel(categoryScore, cat.maxScore);
    return {
      ...cat,
      hazardCount,
      categoryScore,
      tier,
      displayLine: `${cat.title}: ${tier} (${categoryScore}/${cat.maxScore})`,
    };
  });

/** Score Insights / change-note style aggregate text — no individual hazard names. */
export const describeEsSelfAssessment = (data = {}) => {
  const parts = [];
  ES_CATEGORIES.forEach((cat) => {
    const n = countChecked(data, cat.keys);
    if (n === 1) {
      parts.push(`1 safety concern flagged in the ${cat.areaLabel} area`);
    } else if (n > 1) {
      parts.push(`${n} safety concerns flagged in the ${cat.areaLabel} area`);
    }
  });

  if (parts.length) return parts;

  const allKeys = ES_CATEGORIES.flatMap((cat) => cat.keys);
  const total = countChecked(data, allKeys);
  if (total === 0) return [];
  if (total === 1) return ["1 safety hazard flagged"];
  return [`${total} safety hazards flagged`];
};
